import React, { PropsWithChildren, useCallback, useMemo } from 'react'

import { Col, Row } from '@amzn/stencil-react-components/layout'
import { useBreakpoints } from '@amzn/stencil-react-components/responsive'

import { IconWithTooltip } from 'src/components/IconWithTooltip'
import { ItemEntityField } from 'src/components/ItemEntityField'
import { ResponsiveRow } from 'src/components/ResponsiveRow'
import { ItemDTO } from 'src/models/dto/items/ItemDTO'
import { Locale } from 'src/models/dto/Locale'
import {
    ItemEditorCheckboxInput,
    ItemEditorTextInput,
} from 'src/pages/module-builder/item-editors/ItemEditorInputs'

export interface ItemHandler {
    updateLabel: (itemId: string, value: string) => void
    updateOptional?: (itemId: string, value: boolean) => void
    updatePreserveOrder?: (itemId: string, value: boolean) => void
}

// Props interface for the component
export interface ItemEditorBasicInfoProps {
    handler: ItemHandler
    itemId: string
    workflowEntityId: string
    locale: Locale
    editDisabled?: boolean
    showValidationError?: boolean
    disableOptional?: boolean
    disablePreserveOrder?: boolean
}

const ItemEditorBasicInfoInner = ({
    handler,
    itemId,
    workflowEntityId,
    locale,
    editDisabled = false,
    showValidationError = false,
    disableOptional = false,
    disablePreserveOrder = false,
    children,
}: PropsWithChildren<ItemEditorBasicInfoProps>) => {
    const { matches } = useBreakpoints()

    const gridGap = matches.s ? 'S100' : matches.m ? 'S200' : 'S300'
    const itemIdFlex = `0 1 ${matches.s ? '100%' : matches.m ? '50%' : '450px'}`
    const optionalFlex = `0 0 ${matches.s ? '100%' : 'auto'}`
    const preserveOrderCheckboxFlex = `0 0 ${matches.s ? '100%' : 'auto'}`
    const padding = useMemo(() => ({ top: `${matches.s ? '8px' : '35px'}` }), [matches.s])

    // Handler methods for updating values with error handling
    const updateLabel = useCallback(
        (nextValue: string) => {
            handler.updateLabel(itemId, nextValue)
        },
        [handler, itemId]
    )

    const updateOptional = useCallback(
        (nextValue: boolean) => {
            /* istanbul ignore else */
            if (handler.updateOptional) {
                handler.updateOptional(itemId, nextValue)
            } else {
                console.error(
                    `ItemEditorBasicInfo: The updateOptional handler for item ${itemId} is missing.`,
                    'Please set disableOptional to be true to remove the checkbox'
                )
            }
        },
        [handler, itemId]
    )

    const updatePreserveOrder = useCallback(
        (nextValue: boolean) => {
            /* istanbul ignore else */
            if (handler.updatePreserveOrder) {
                handler.updatePreserveOrder(itemId, nextValue)
            } else {
                console.error(
                    `ItemEditorBasicInfo: The updatePreserveOrder handler for item ${itemId} is missing.`,
                    'Please set disablePreserveOrder to be true to remove the checkbox'
                )
            }
        },
        [handler, itemId]
    )

    const commonFieldProps = useMemo(
        () => ({
            workflowEntityId,
            itemId,
            locale,
            showValidationError,
            editDisabled,
        }),
        [itemId, locale, showValidationError, editDisabled, workflowEntityId]
    )

    const fieldsPart = useMemo(
        () => (
            <>
                <Col flex={itemIdFlex} width='100%'>
                    <ItemEntityField<ItemDTO, string>
                        {...commonFieldProps}
                        derive={(item) => item.label}
                        deriveValidationErrorMessage={(errors) =>
                            (errors.validationErrors.label ?? []).join(', ')
                        }
                    >
                        {({ value, props }) => (
                            <ItemEditorTextInput
                                {...props}
                                dataTestId={'unique-item-id'}
                                inputId={'unique-item-id'}
                                value={value}
                                placeholder={'Some human readable label'}
                                setValue={updateLabel}
                                labelText={'Unique Item ID'}
                            />
                        )}
                    </ItemEntityField>
                </Col>
                {!disableOptional && (
                    <Col gridGap={'S200'} padding={padding} flex={optionalFlex}>
                        <ItemEntityField<ItemDTO, boolean>
                            {...commonFieldProps}
                            derive={(item) => item.optional}
                            deriveValidationErrorMessage={() => undefined}
                        >
                            {({ value, props }) => (
                                <ItemEditorCheckboxInput
                                    {...props}
                                    dataTestId={'optional-checkbox'}
                                    inputId={'optional'}
                                    labelText={'Optional'}
                                    value={value}
                                    setValue={updateOptional}
                                />
                            )}
                        </ItemEntityField>
                    </Col>
                )}
                {!disablePreserveOrder && (
                    <Col gridGap={'S200'} padding={padding} flex={preserveOrderCheckboxFlex}>
                        <Row alignItems={'center'}>
                            <ItemEntityField<ItemDTO, boolean>
                                {...commonFieldProps}
                                derive={(item) => item.preserveOrder}
                                deriveValidationErrorMessage={() => undefined}
                            >
                                {({ value, props }) => (
                                    <ItemEditorCheckboxInput
                                        {...props}
                                        dataTestId='preserve-order-checkbox'
                                        inputId={'preserve-order'}
                                        labelText={'Preserve order'}
                                        value={value}
                                        setValue={updatePreserveOrder}
                                    />
                                )}
                            </ItemEntityField>
                            <IconWithTooltip tooltipText='When randomizing page, ensure this items position is never changed' />
                        </Row>
                    </Col>
                )}
            </>
        ),
        [
            commonFieldProps,
            disableOptional,
            disablePreserveOrder,
            itemIdFlex,
            optionalFlex,
            padding,
            preserveOrderCheckboxFlex,
            updateLabel,
            updateOptional,
            updatePreserveOrder,
        ]
    )
    return (
        <ResponsiveRow gridGap={gridGap} alignItems={'flex-start'} flexWrap={'wrap'}>
            {fieldsPart}
            {children}
        </ResponsiveRow>
    )
}
export const ItemEditorBasicInfo = React.memo(ItemEditorBasicInfoInner)
ItemEditorBasicInfo.displayName = 'ItemEditorBasicInfo'
